/**
 * TermsPage.tsx — /legal/terms — ɳSentry SaaS Terms of Service
 *
 * Purpose:    Terms of Service for the hosted service at sentry.nself.org:
 *             subscriptions/billing (Stripe), cancellation & refunds,
 *             acceptable use, early-access SLA disclaimer, data retention
 *             per tier, and termination.
 * Inputs:     Static content; retention figures mirror lib/tiers.ts
 * Outputs:    Legal document page
 * Constraints: Standard SaaS template, marked "review with counsel".
 * SOT:        nsentry SaaS completion spec § G-WEB (legal)
 */
import { Link } from 'react-router-dom'
import { LegalLayout } from './LegalLayout'

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="2026-07-03">
      <section>
        <h2 id="acceptance">1. Acceptance of these terms</h2>
        <p>
          These Terms of Service (&quot;Terms&quot;) govern your use of the hosted ɳSentry
          service at <strong>sentry.nself.org</strong> and <strong>api.sentry.nself.org</strong>{' '}
          (the &quot;Service&quot;), operated by the ɳSelf project (&quot;we&quot;,
          &quot;us&quot;). By creating an account or using the Service you agree to these
          Terms and to our <Link to="/legal/privacy">Privacy Policy</Link>. If you do not
          agree, do not use the Service. The open-source ɳSentry software you self-host is
          licensed separately (MIT) and is not covered by these Terms.
        </p>
      </section>

      <section>
        <h2 id="service">2. The Service &amp; early access</h2>
        <p>
          The Service provides uptime monitoring, status pages, incident management, error
          tracking, real-user monitoring, and cron/heartbeat monitoring as a hosted
          offering. The Service is currently in <strong>early access</strong>:
        </p>
        <ul>
          <li>
            <strong>No SLA.</strong> We do not yet offer a contractual uptime or support
            service-level agreement. The Service is provided &quot;as is&quot; and
            &quot;as available&quot;, without warranty of any kind.
          </li>
          <li>Features may change, and maintenance windows may occur without notice.</li>
          <li>
            Monitoring alerts are a convenience, not a guarantee — you remain responsible
            for the availability of your own systems.
          </li>
        </ul>
      </section>

      <section>
        <h2 id="accounts">3. Accounts</h2>
        <p>
          You must provide a valid email address and keep your credentials confidential.
          You are responsible for all activity under your account, including team members
          you invite. You must be at least 16 years old and legally able to enter this
          agreement.
        </p>
      </section>

      <section>
        <h2 id="billing">4. Subscriptions, billing &amp; refunds</h2>
        <ul>
          <li>
            <strong>Free tier.</strong> The free tier requires no payment card and is
            subject to the quotas shown on the <Link to="/pricing">pricing page</Link>.
          </li>
          <li>
            <strong>Paid plans.</strong> Paid plans (ɳSentry Bundle from $0.99/month, and
            ɳSelf+) are billed as recurring subscriptions through{' '}
            <strong>Stripe</strong>, our payment processor. We never see or store your
            full card details.
          </li>
          <li>
            <strong>Renewal &amp; cancellation.</strong> Subscriptions renew automatically
            each billing period until cancelled. You can cancel at any time from Billing →
            Manage billing (the Stripe customer portal); your plan stays active until the
            end of the paid period, then downgrades to the free tier.
          </li>
          <li>
            <strong>Refunds.</strong> Contact <a href="mailto:billing@nself.org">billing@nself.org</a>{' '}
            within 14 days of a charge for a full refund of that charge. After 14 days,
            charges are non-refundable except where required by law.
          </li>
          <li>
            <strong>Price changes</strong> take effect at your next renewal and will be
            announced at least 30 days in advance.
          </li>
          <li>
            <strong>Over-quota use.</strong> Ingest beyond your plan&apos;s monthly quotas
            (error events, RUM pageviews) may be rejected until the quota resets or you
            upgrade.
          </li>
        </ul>
      </section>

      <section>
        <h2 id="acceptable-use">5. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>monitor, probe, or load-test systems you do not own or lack permission to monitor;</li>
          <li>use the Service to send spam, host malware, or facilitate illegal activity;</li>
          <li>attempt to breach tenant isolation, access another customer&apos;s data, or circumvent quotas or authentication;</li>
          <li>resell the Service without a separate written agreement;</li>
          <li>submit content to public status pages that is unlawful, deceptive, or infringes third-party rights.</li>
        </ul>
        <p>
          We may suspend or terminate accounts that violate this section, with notice where
          practicable.
        </p>
      </section>

      <section>
        <h2 id="your-data">6. Your data &amp; retention</h2>
        <p>
          You own the data you send to the Service. You grant us a limited licence to
          store and process it solely to operate the Service. Monitoring data is retained
          per your plan — <strong>7 days</strong> on the free tier, 90 days on the ɳSentry
          Bundle, and 365 days on ɳSelf+, as listed on the{' '}
          <Link to="/pricing">pricing page</Link> — and is
          deleted or aggregated after the retention window. Account deletion permanently
          removes your tenant and all its data (see{' '}
          <Link to="/legal/gdpr">GDPR &amp; Data Processing</Link> for export and deletion
          flows).
        </p>
      </section>

      <section>
        <h2 id="liability">7. Disclaimer &amp; limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, we exclude all implied warranties and
          are not liable for indirect, incidental, special, or consequential damages,
          including losses caused by missed or delayed alerts, downtime, or data loss.
          Our total aggregate liability for any claim is limited to the amount you paid
          us for the Service in the 12 months preceding the claim (or $10 if you paid
          nothing).
        </p>
      </section>

      <section>
        <h2 id="termination">8. Termination</h2>
        <p>
          You may delete your account at any time from Settings. We may suspend or
          terminate the Service for material breach of these Terms, non-payment, or where
          required by law. On termination we delete your tenant data within 30 days,
          except minimal billing records we must retain for tax and accounting law.
        </p>
      </section>

      <section>
        <h2 id="changes">9. Changes to these terms</h2>
        <p>
          We may update these Terms. Material changes will be announced by email or an
          in-app notice at least 14 days before they take effect. Continued use after the
          effective date constitutes acceptance.
        </p>
      </section>

      <section>
        <h2 id="contact">10. Contact</h2>
        <p>
          Legal notices: <a href="mailto:legal@nself.org">legal@nself.org</a>. Billing:{' '}
          <a href="mailto:billing@nself.org">billing@nself.org</a>.
        </p>
      </section>
    </LegalLayout>
  )
}
